import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CalendarX2, ArrowLeft } from "lucide-react";

/**
 * Landing page shown when a user tries to open or submit a survey whose
 * phase deadline has already passed. Reached in two ways:
 *   - Internal: /app/projects/:id/surveys/:phase/closed  (SurveyFillPage redirect)
 *   - Public : /s/:token/closed                          (PublicSurveyPage redirect)
 *
 * We keep the copy generic (Portuguese) and offer a graceful way back:
 * either the project detail page (authenticated) or the IHTP landing site
 * (anonymous). The `closed_at` query param lets us display when the survey
 * closed so users understand why they were redirected.
 */
export default function SurveyClosedPage() {
    const params = useParams();
    const [search] = useSearchParams();
    const closedAt = search.get("closed_at");
    const projectId = params.id || null; // present only on the authenticated route
    const formattedClosed = closedAt ? _prettyDate(closedAt) : null;
    return (
        <div className="min-h-screen flex items-center justify-center bg-[#fdfcf9] p-6" data-testid="survey-closed-page">
            <div className="max-w-lg w-full border border-[#e4e4e7] bg-white p-8 md:p-10 text-center">
                <div className="w-14 h-14 rounded-full bg-[#f3ede0] flex items-center justify-center mx-auto">
                    <CalendarX2 size={26} className="text-[#b89b6f]" />
                </div>
                <h1 className="font-display text-4xl text-[#0d1628] mt-6" data-testid="survey-closed-title">
                    Inquérito encerrado
                </h1>
                <p className="text-[#52525b] mt-3">
                    {formattedClosed
                        ? `O prazo para responder a este inquérito terminou a ${formattedClosed}.`
                        : "O prazo para responder a este inquérito já terminou."}
                </p>
                <p className="text-sm text-[#71717a] mt-4">
                    Se necessita mesmo de submeter as suas respostas, contacte o gestor do projeto para reabrir o inquérito.
                </p>
                <div className="mt-8 flex flex-wrap gap-3 justify-center">
                    {projectId ? (
                        <Link to={`/app/projects/${projectId}`} className="btn-outline" data-testid="closed-back-to-project">
                            <ArrowLeft size={14} /> Voltar ao projeto
                        </Link>
                    ) : (
                        <Link to="/" className="btn-outline" data-testid="closed-back-to-home">
                            <ArrowLeft size={14} /> Voltar ao início
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
}

function _prettyDate(iso) {
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return null;
        return d.toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" });
    } catch {
        return null;
    }
}
